import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Sparkles, Flame, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { flyToCart } from '../lib/flyToCart';

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { settings } = useSettings();
  const threshold = Math.max(1, parseInt(settings?.low_stock_threshold, 10) || 5);
  const outOfStock = product.stock === 0;
  const lowStock = product.stock > 0 && product.stock <= threshold;
  const price = product.is_offer && product.offer_price ? product.offer_price : product.price;
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    if (imgRef.current) flyToCart(imgRef.current);
    addItem(product);
  };

  return (
    <Link
      to={`/producto/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="soft-card product-card-glow group flex flex-col overflow-hidden cursor-pointer h-full"
    >
      <div className="relative aspect-square bg-pinky-50 overflow-hidden">
        {product.image ? (
          <>
            {/* Shimmer placeholder mientras carga la miniatura */}
            {!imgLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-pink-50 via-pink-100 to-pink-50 animate-pulse" />
            )}
            <img
              ref={imgRef}
              src={product.image}
              alt={product.name}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              loading="lazy"
              decoding="async"
              onLoad={() => setImgLoaded(true)}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-pinky-300">
            <Sparkles className="h-16 w-16" strokeWidth={1.2} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.is_new && (
            <span className="px-2.5 py-1 rounded-full bg-pink-100 text-pink-700 text-xs font-bold border border-pink-200 flex items-center gap-1">
              <Sparkles className="h-3 w-3" strokeWidth={2} /> Nuevo
            </span>
          )}
          {product.is_offer && (
            <span className="px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold border border-purple-200 flex items-center gap-1">
              <Flame className="h-3 w-3" strokeWidth={2} /> Oferta
            </span>
          )}
          {lowStock && (
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" strokeWidth={2} /> Pocas unidades
            </span>
          )}
        </div>
        {outOfStock && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
            <span className="px-4 py-2 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-bold text-sm">Agotado</span>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1 gap-3">
        <div className="flex-1">
          <h3 className="font-display text-lg leading-snug text-slate-900 line-clamp-2 group-hover:text-pinky-600 transition-colors">{product.name}</h3>
          {product.description && (
            <p className="mt-1 text-sm text-slate-500 line-clamp-2">{product.description}</p>
          )}
        </div>

        <div className="flex items-baseline gap-2">
          <span data-testid={`product-price-${product.id}`} className="font-display text-2xl text-pinky-600 font-bold">Q{price.toFixed(0)}</span>
          {product.is_offer && product.offer_price && (
            <span className="text-sm text-slate-400 line-through">Q{product.price.toFixed(0)}</span>
          )}
        </div>

        <div className="flex flex-col items-stretch gap-2 pt-2 border-t border-pinky-100 sm:flex-row sm:items-center sm:justify-between">
          <span className={`text-xs font-semibold ${outOfStock ? 'text-rose-600' : lowStock ? 'text-amber-700' : 'text-emerald-700'}`}>
            {outOfStock ? 'Agotado' : lowStock ? `Solo ${product.stock} disp.` : `${product.stock} disponibles`}
          </span>
          <Button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            disabled={outOfStock}
            size="sm"
            className="btn-pink rounded-full font-semibold gap-2 h-11 sm:h-9 w-full sm:w-auto"
          >
            <ShoppingBag className="h-4 w-4" strokeWidth={2} /> Agregar
          </Button>
        </div>
      </div>
    </Link>
  );
}
